import { useEffect, useState, useCallback } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  KanbanSquare,
  CheckSquare,
  Users,
  LogOut,
  Globe,
  Plus,
  Search,
  Upload,
  Trash2,
  Settings,
  Menu,
  X,
  Clock,
  BookOpen,
  Wallet,
  Headphones,
  TrendingUp,
  Activity,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { CRMSettingsProvider, useStageLabel } from "../../context/CRMSettingsContext";
import { LeadFocusProvider } from "../../context/LeadFocusContext";
import { tCrm, crmApi, auth, formatCurrency, STAGES, STAGE_COLORS, logDevError } from "../../lib/crm";
import { toast } from "sonner";
import CRMLeads from "./CRMLeads";
import CRMLeadDetail from "./CRMLeadDetail";
import CRMTasks from "./CRMTasks";
import CRMAgents from "./CRMAgents";
import CRMSettings from "./CRMSettings";
import CRMResources from "./CRMResources";
import CRMCommissions from "./CRMCommissions";
import CRMCallStrategies from "./CRMCallStrategies";
import CRMAgentActivity from "./CRMAgentActivity";
import MailtoSetupBanner from "./MailtoSetupBanner";
import AgentNudgeBanner from "./AgentNudgeBanner";
import ClickTracker from "./ClickTracker";
import NotificationBell from "./NotificationBell";
import AgentsPresenceBadge from "./AgentsPresenceBadge";
import TimezoneManager from "./TimezoneManager";
import TimezoneAgentView from "./TimezoneAgentView";
import TopClockBar from "./TopClockBar";

export default function CRMApp() {
  return (
    <CRMSettingsProvider>
      <LeadFocusProvider>
        <CRMAppInner />
      </LeadFocusProvider>
    </CRMSettingsProvider>
  );
}

function CRMAppInner() {
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [me, setMe] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [myTzProfile, setMyTzProfile] = useState(null);

  useEffect(() => {
    if (!auth.isAuthed()) {
      navigate("/crm/login", { replace: true });
      return;
    }
    crmApi.get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        auth.clear();
        navigate("/crm/login", { replace: true });
      });
  }, [navigate]);

  // Fetch agent's own tz profile (used to show local clock in top bar)
  useEffect(() => {
    if (!me || me.role !== "agent") return;
    crmApi.get("/timezones/my-view")
      .then((r) => setMyTzProfile(r.data?.agent || null))
      .catch((err) => logDevError("CRMApp.myTzView", err));
  }, [me]);

  // Country clocks: derive unique timezones from this agent's assigned leads,
  // using country FIRST and falling back to city/city_area when country is empty
  // (e.g. when a prospect list dumps everything into the City column).
  const [leadLocations, setLeadLocations] = useState([]);
  useEffect(() => {
    if (!me || me.role !== "agent") return undefined;
    const loadCountries = () => {
      crmApi
        .get("/leads", { params: { include_lost: false } })
        .then((r) => {
          // De-duplicate by the (country|city|city_area) tuple — TopClockBar will
          // resolve each one and skip those whose resolved tz collides with another
          // already-rendered pill.
          const seen = new Set();
          const locations = [];
          for (const l of r.data || []) {
            const key = `${(l.country || "").trim()}|${(l.city || "").trim()}|${(l.city_area || "").trim()}`;
            if (key === "||") continue;
            if (seen.has(key)) continue;
            seen.add(key);
            locations.push({
              country: l.country || "",
              city: l.city || "",
              city_area: l.city_area || "",
            });
          }
          setLeadLocations(locations);
        })
        .catch((err) => logDevError("CRMApp.leadLocations", err));
    };
    loadCountries();
    const id = setInterval(loadCountries, 5 * 60_000);
    return () => clearInterval(id);
  }, [me]);

  // Heartbeat — keeps the admin's presence view live for this agent.
  // Pings on mount + every 60s. Backend stores last_seen_at; admin polls /presence.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    const ping = () => {
      crmApi.post("/agents/heartbeat").catch(() => {});
    };
    ping();
    const id = setInterval(() => {
      if (!cancelled) ping();
    }, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [me]);

  const logout = () => {
    auth.clear();
    navigate("/crm/login", { replace: true });
  };

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] text-white/60 text-sm">
        {t("common.loading")}
      </div>
    );
  }

  const isAdmin = me.role === "admin";

  const navItems = [
    { to: "/crm", icon: LayoutDashboard, label: t("nav.dashboard"), end: true },
    { to: "/crm/leads", icon: KanbanSquare, label: t("nav.leads") },
    { to: "/crm/tasks", icon: CheckSquare, label: t("nav.tasks") },
    { to: "/call-assistant.html", icon: Headphones, label: t("nav.callAssistant"), external: true },
    { to: "/crm/commissions", icon: Wallet, label: t("nav.commissions") },
    ...(isAdmin ? [{ to: "/crm/call-strategies", icon: TrendingUp, label: t("nav.callStrategies") }] : []),
    ...(isAdmin ? [{ to: "/crm/agent-activity", icon: Activity, label: t("nav.agentActivity") }] : []),
    { to: "/crm/resources", icon: BookOpen, label: t("nav.resources") },
    { to: "/crm/timezones", icon: Clock, label: t("nav.timezones") },
    ...(isAdmin ? [{ to: "/crm/agents", icon: Users, label: t("nav.agents") }] : []),
    ...(isAdmin ? [{ to: "/crm/settings", icon: Settings, label: t("nav.settings") }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 w-60 h-screen bg-[#0F0F17] border-r border-white/8 flex flex-col z-40 transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <Link to="/crm" className="flex items-center gap-3 p-5 border-b border-white/8">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/kuo8ppes_Logo%20Global%20tech%20500x500%20FT.png"
              alt="GTX"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="font-bold text-sm text-white">GTX CRM</div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mono">
              {isAdmin ? "Admin" : "Agent"}
            </div>
          </div>
        </Link>

        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => {
            if (item.external) {
              return (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className="flex items-center gap-3 px-3 py-2 text-sm rounded-md transition text-white/60 hover:text-white hover:bg-white/5"
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                  <span className="ml-auto text-[9px] uppercase tracking-wider text-lime-400/70">↗</span>
                </a>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm rounded-md transition ${
                    isActive
                      ? "bg-lime-400/15 text-lime-300 border border-lime-400/20"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/8 space-y-2">
          <div className="px-3 py-2 text-xs">
            <div className="text-white/80 font-medium truncate">{me.full_name || "Admin"}</div>
            <div className="text-white/40 mono text-[10px] truncate">{me.email || "admin"}</div>
          </div>
          <button
            onClick={toggleLanguage}
            data-testid="crm-lang-toggle"
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-md transition"
          >
            <Globe className="w-3.5 h-3.5" />
            {language.toUpperCase()} / {language === "es" ? "EN" : "ES"}
          </button>
          <button
            onClick={logout}
            data-testid="crm-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400/80 hover:text-red-300 hover:bg-red-500/10 rounded-md transition"
          >
            <LogOut className="w-3.5 h-3.5" />
            {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="lg:ml-60 min-h-screen">
        {/* Top utility bar (mobile menu + notifications) */}
        <div className="sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6 py-3 bg-[#0A0A0F]/80 backdrop-blur-md border-b border-white/5">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-2 text-white/70 hover:text-white"
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="lg:hidden font-bold text-sm">GTX CRM</div>
          <div className="flex items-center gap-2 ml-auto">
            {isAdmin && <AgentsPresenceBadge />}
            <TopClockBar
              agentTz={myTzProfile?.timezone}
              agentCity={myTzProfile?.city}
              leadLocations={leadLocations}
            />
            <NotificationBell />
          </div>
        </div>
        <MailtoSetupBanner />
        <AgentNudgeBanner enabled={!!me && me.role === "agent"} />
        <ClickTracker enabled={!!me && me.role !== "admin"} />
        <Routes>
          <Route index element={<Dashboard me={me} />} />
          <Route path="leads" element={<CRMLeads me={me} />} />
          <Route path="leads/:id" element={<CRMLeadDetail me={me} />} />
          <Route path="tasks" element={<CRMTasks me={me} />} />
          <Route path="commissions" element={<CRMCommissions me={me} />} />
          {isAdmin && <Route path="call-strategies" element={<CRMCallStrategies />} />}
          {isAdmin && <Route path="agent-activity" element={<CRMAgentActivity />} />}
          <Route path="resources" element={<CRMResources me={me} />} />
          <Route path="timezones" element={isAdmin ? <TimezoneManager /> : <TimezoneAgentView />} />
          {isAdmin && <Route path="agents" element={<CRMAgents />} />}
          {isAdmin && <Route path="settings" element={<CRMSettings />} />}
          <Route path="*" element={<Navigate to="/crm" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// ============================================================
// Dashboard
// ============================================================
function Dashboard({ me }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const stageLabel = useStageLabel();
  const [stats, setStats] = useState(null);
  const isAdmin = me.role === "admin";

  useEffect(() => {
    crmApi.get("/stats").then((r) => setStats(r.data)).catch((err) => logDevError("CRMApp.stats", err));
  }, []);

  if (!stats) {
    return <div className="p-10 text-white/40 text-sm">{t("common.loading")}</div>;
  }

  const cards = [
    { label: t("dash.newThisMonth"), value: stats.new_this_month, color: "text-lime-300" },
    { label: t("dash.pipelineValue"), value: formatCurrency(stats.pipeline_value), color: "text-purple-300" },
    { label: t("dash.wonThisMonth"), value: `${stats.won_count_month} · ${formatCurrency(stats.won_value_month)}`, color: "text-green-300" },
    { label: t("dash.convRate"), value: `${stats.conv_rate}%`, color: "text-amber-300" },
  ];

  const goalProgress = stats.my_goal ? Math.min(100, (stats.my_progress / stats.my_goal) * 100) : 0;

  return (
    <div className="p-8 space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("nav.dashboard")}</h1>
          <p className="text-sm text-white/40 mt-1">{me.full_name || "Admin"}</p>
        </div>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-cards">
        {cards.map((c) => (
          <div key={c.label} className="glass-strong rounded-2xl p-5 border border-white/5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mono">{c.label}</div>
            <div className={`mt-3 text-2xl font-bold ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Agent goal progress */}
      {!isAdmin && stats.my_goal > 0 && (
        <div className="glass-strong rounded-2xl p-6 border border-white/5">
          <div className="flex justify-between text-sm mb-3">
            <span className="text-white/60">{t("dash.myGoal")}</span>
            <span className="font-mono">{formatCurrency(stats.my_progress)} / {formatCurrency(stats.my_goal)}</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-lime-400 to-emerald-400 transition-all"
              style={{ width: `${goalProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Stage distribution */}
        <div className="glass-strong rounded-2xl p-6 border border-white/5">
          <h3 className="text-sm font-semibold text-white/80 mb-4">{t("dash.stageDistribution")}</h3>
          <div className="space-y-2">
            {STAGES.map((s) => {
              const count = stats.stage_counts[s] || 0;
              const total = Object.values(stats.stage_counts).reduce((a, b) => a + b, 0) || 1;
              const pct = (count / total) * 100;
              return (
                <div key={s}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/60 capitalize">{stageLabel(s, language, t(`stage.${s}`))}</span>
                    <span className="text-white/80 mono">{count}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: STAGE_COLORS[s] }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Source / Lost reasons */}
        <div className="space-y-4">
          <DistributionCard title={t("dash.sourceDistribution")} data={stats.source_counts} />
          <DistributionCard title={t("dash.lostReasons")} data={stats.lost_reasons} empty={t("dash.noData")} />
        </div>
      </div>

      {/* Agent ranking (admin only) */}
      {isAdmin && stats.agent_ranking?.length > 0 && (
        <div className="glass-strong rounded-2xl p-6 border border-white/5">
          <h3 className="text-sm font-semibold text-white/80 mb-4">{t("dash.agentRanking")}</h3>
          <div className="space-y-2">
            {stats.agent_ranking.map((a, i) => (
              <div key={a.agent_id} className="flex items-center justify-between text-sm p-2 rounded-md hover:bg-white/5">
                <div className="flex items-center gap-3">
                  <span className="w-6 text-center text-white/40 mono">{i + 1}</span>
                  <span className="font-medium">{a.full_name}</span>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-white/40">{a.leads} leads</span>
                  <span className="text-emerald-400">{a.won} won</span>
                  <span className="font-mono">{formatCurrency(a.value)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4 text-sm">
        <div className="glass-strong rounded-xl px-4 py-3 border border-white/5">
          <span className="text-white/40 mr-2">{t("dash.pendingTasks")}:</span>
          <span className="font-bold">{stats.pending_tasks}</span>
        </div>
        <div className="glass-strong rounded-xl px-4 py-3 border border-red-500/20 bg-red-500/5">
          <span className="text-red-300/70 mr-2">{t("dash.overdueTasks")}:</span>
          <span className="font-bold text-red-300">{stats.overdue_tasks}</span>
        </div>
      </div>
    </div>
  );
}

function DistributionCard({ title, data, empty }) {
  const entries = Object.entries(data || {});
  const total = entries.reduce((a, [, v]) => a + v, 0);
  return (
    <div className="glass-strong rounded-2xl p-6 border border-white/5">
      <h3 className="text-sm font-semibold text-white/80 mb-4">{title}</h3>
      {entries.length === 0 ? (
        <div className="text-xs text-white/30">{empty || "—"}</div>
      ) : (
        <div className="space-y-2">
          {entries.map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs">
              <span className="text-white/60 capitalize">{k.replace(/_/g, " ")}</span>
              <span className="font-mono">
                {v} <span className="text-white/30">({Math.round((v / (total || 1)) * 100)}%)</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
